import { BellOutlined, InboxOutlined } from '@ant-design/icons';
import { Badge, Button, Popover, Row, Space, Tooltip, Typography, theme } from 'antd';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { WsMessage } from '../../../clients/chat/types';
import useChatMessagesUpdates from '../../../hooks/plugins/chat/useChatMessagesUpdates';
import useToggle from '../../../hooks/useToggle';
import endPoints from '../../../services/endpoints';
import { getSelectedTabKeyFromUrl } from '../../../store/childStore/tabStore';
import { pushTo } from '../../../utils/routeHelper';
import { filterSWRKeys } from '../../../utils/swr';
import MessageList from './MessageList';

interface Props {
    isOnline: boolean;
}

// last message from the same ticketKey will be replaced with the old one
// This component should decide to update badgeIcon and messageList, depending on the related chat tab is open or not / active or not
// chat tab open: send last message to the tabStore to update chat tab, and then ring the message bell
// chat tab open and active: send message via custom event to handle in AgentChatComponent
// chat tab open and not active: update the chat tab badge, also
// chat tab closed: handle new messages UI-interaction on below components, and then ring the message bell

const notRelatedWithSelectedTicketKey = (ticketKey: string) => (item: { ticketKey: string; message: WsMessage }) =>
    item.ticketKey !== ticketKey;

// @isOnline prop controls to receive messages from a chat destination, and to disable message-list-dropdown button or not
export default function ChatMessagesUpdates({ isOnline: _isOnline }: Props) {
    const {
        token: { fontSizeXL, paddingSM, zIndexBase }
    } = theme.useToken();

    const { t } = useTranslation('common');

    const { mutate } = useSWRConfig();
    const { asPath } = useRouter();
    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);

    const [popoverVisible, toggle, , close] = useToggle();
    const audioEl = useRef<HTMLAudioElement>(null);
    const { messages, setMessages } = useChatMessagesUpdates(audioEl, selectedTabKey);

    const onMessageClick = async (ticketKey: string): Promise<void> => {
        // console.debug(`ChatMessagesUpdates, onMessageClick, ticketKey:`, ticketKey);
        close();
        setMessages((prev) => prev.filter(notRelatedWithSelectedTicketKey(ticketKey)));

        await pushTo(`/tickets/${ticketKey}`);
        await mutate(filterSWRKeys(endPoints.chat.activeChatEntities));
    };

    // FIXME cagri: chat-module preventing audio play Error: Uncaught (in promise) DOMException: play() failed because the user didn't interact with the document first.
    return (
        <>
            <audio ref={audioEl} src="https://bigsoundbank.com/UPLOAD/ogg/2038.ogg" preload="auto" />
            <Space size="small">
                <Badge count={messages?.length ?? 0} size="small" overflowCount={10} style={{ zIndex: zIndexBase + 1 }}>
                    <Popover
                        overlayStyle={{ width: '45ch' }}
                        overlayInnerStyle={{
                            paddingBottom: paddingSM,
                            paddingLeft: paddingSM,
                            paddingRight: 0,
                            paddingTop: paddingSM
                        }}
                        content={<MessageList data={messages} onMessageClick={onMessageClick} />}
                        title={
                            <Row justify="space-between" align="middle" style={{ paddingRight: paddingSM }}>
                                <Typography.Text strong>{t('plugin.chat.updates')}</Typography.Text>
                                <Tooltip title={t('plugin.chat.markAsRead')} placement="left">
                                    <Button
                                        shape="circle"
                                        icon={<InboxOutlined style={{ fontSize: fontSizeXL }} />}
                                        disabled={messages?.length === 0}
                                        onClick={() => setMessages([])}
                                    />
                                </Tooltip>
                            </Row>
                        }
                        trigger="click"
                        open={popoverVisible}
                        placement="bottomRight"
                        destroyTooltipOnHide={false}
                    >
                        <Button
                            type="default"
                            icon={<BellOutlined />}
                            style={{ backgroundColor: 'transparent' }}
                            // disabled={!isOnline}
                            onClick={toggle}
                        />
                    </Popover>
                </Badge>
            </Space>
        </>
    );
}
